import { useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@mui/joy";
import { Button, ControlledTextField, FormRow } from "../../../../../../shared";
import { useProject } from "../../../../../../hooks/query";

export function Customer({ details }: { details: any | null }) {
    const projectId = details?.Id as string | undefined;
    const { useProjectAttributeUpdate } = useProject();
    const updateProjectAttributeSvc = useProjectAttributeUpdate();

    const [name, setName] = useState<string>(details?.CustomerName ?? "");
    const [phone, setPhone] = useState<string>(details?.CustomerPhoneNumber ?? "");
    const [email, setEmail] = useState<string>(details?.CustomerEmail ?? "");
    const [street, setStreet] = useState<string>(details?.CustomerStreet ?? "");
    const [city, setCity] = useState<string>(details?.CustomerCity ?? "");
    const [state, setState] = useState<string>(details?.CustomerState ?? "");
    const [zip, setZip] = useState<string>(details?.CustomerZipCode ?? "");
    const [country, setCountry] = useState<string>(details?.CustomerCountry ?? "");
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setName(details?.CustomerName ?? "");
        setPhone(details?.CustomerPhoneNumber ?? "");
        setEmail(details?.CustomerEmail ?? "");
        setStreet(details?.CustomerStreet ?? "");
        setCity(details?.CustomerCity ?? "");
        setState(details?.CustomerState ?? "");
        setZip(details?.CustomerZipCode ?? "");
        setCountry(details?.CustomerCountry ?? "");
    }, [
        details?.CustomerName,
        details?.CustomerPhoneNumber,
        details?.CustomerEmail,
        details?.CustomerStreet,
        details?.CustomerCity,
        details?.CustomerState,
        details?.CustomerZipCode,
        details?.CustomerCountry,
    ]);

    const hasChanges = useMemo(() => {
        return (
            (details?.CustomerName ?? "") !== name ||
            (details?.CustomerPhoneNumber ?? "") !== phone ||
            (details?.CustomerEmail ?? "") !== email ||
            (details?.CustomerStreet ?? "") !== street ||
            (details?.CustomerCity ?? "") !== city ||
            (details?.CustomerState ?? "") !== state ||
            (details?.CustomerZipCode ?? "") !== zip ||
            (details?.CustomerCountry ?? "") !== country
        );
    }, [details, name, phone, email, street, city, state, zip, country]);

    const handleUpdate = async () => {
        if (!projectId || !hasChanges) return;
        setSaving(true);
        try {
            const updates: Array<{ Attribute: string; Value: string }> = [];
            if ((details?.CustomerName ?? "") !== name) updates.push({ Attribute: "CustomerName", Value: name });
            if ((details?.CustomerPhoneNumber ?? "") !== phone)
                updates.push({ Attribute: "CustomerPhoneNumber", Value: phone });
            if ((details?.CustomerEmail ?? "") !== email) updates.push({ Attribute: "CustomerEmail", Value: email });
            if ((details?.CustomerStreet ?? "") !== street)
                updates.push({ Attribute: "CustomerStreet", Value: street });
            if ((details?.CustomerCity ?? "") !== city) updates.push({ Attribute: "CustomerCity", Value: city });
            if ((details?.CustomerState ?? "") !== state) updates.push({ Attribute: "CustomerState", Value: state });
            if ((details?.CustomerZipCode ?? "") !== zip) updates.push({ Attribute: "CustomerZipCode", Value: zip });
            if ((details?.CustomerCountry ?? "") !== country)
                updates.push({ Attribute: "CustomerCountry", Value: country });

            for (const u of updates) {
                await updateProjectAttributeSvc.mutateAsync({ Id: projectId, Attribute: u.Attribute, Value: u.Value });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ flex: 1 }}>
            <Box mb={2}>
                <Typography level="title-sm">Customer</Typography>
                <Typography level="body-sm">Manage customer contact and address information.</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Box display="flex" flexDirection="column" gap={2}>
                <FormRow label="Customer Name" description="Full name of the customer.">
                    <ControlledTextField value={name} onChange={setName} />
                </FormRow>
                <FormRow label="Phone" description="Primary contact number.">
                    <ControlledTextField value={phone} onChange={setPhone} />
                </FormRow>
                <FormRow label="Email" description="Customer email address.">
                    <ControlledTextField type="email" value={email} onChange={setEmail} />
                </FormRow>
                <FormRow label="Street" description="Street address.">
                    <ControlledTextField value={street} onChange={setStreet} />
                </FormRow>
                <FormRow label="City" description="City.">
                    <ControlledTextField value={city} onChange={setCity} />
                </FormRow>
                <FormRow label="State" description="State/Province.">
                    <ControlledTextField value={state} onChange={setState} />
                </FormRow>
                <FormRow label="Zip" description="Postal code.">
                    <ControlledTextField value={zip} onChange={setZip} />
                </FormRow>
                <FormRow label="County" description="County where the customer is located.">
                    <ControlledTextField value={country} onChange={setCountry} />
                </FormRow>

                <Box>
                    <Button
                        onClick={handleUpdate}
                        disabled={!hasChanges || saving}
                        loading={saving}
                        loadingPosition="end"
                    >
                        {saving ? "Updating" : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
